import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

// The vehicle, drawn from its own layout.
//
// Not a ten-wheel picture with the numbers moved around: the axles, how many wheels each carries and
// which side they sit on all come from the layout, so a four-tyre van, an eighteen-tyre tractor and
// trailer, and whatever somebody describes next all draw correctly with no new code. A fitter
// reading this should be able to point at the tyre on the screen and then walk to it.
//
// Colour alone never carries the state. Each wheel also shows its reading and, when it is not
// normal, a word - because a tyre workshop is a bad place to rely on telling red from amber.
const GEOMETRY = {
  wheel: { w: 46, h: 74 },
  gap: 8, // between the two wheels of a dual pair
  hub: 26, // half-width of the axle beam either side of centre
  axleGap: 118,
  margin: 34,
};

// Where each wheel sits relative to the chassis centre line. Outer wheels sit further out, which is
// what makes a dual pair read as a dual pair.
const OFFSETS = {
  L: [-1], R: [1], LO: [-2], LI: [-1], RI: [1], RO: [2],
};

const palette = (theme) => ({
  NORMAL: { fill: theme.palette.success.main, text: theme.palette.success.contrastText, label: 'Normal' },
  WARNING: { fill: theme.palette.warning.main, text: theme.palette.warning.contrastText, label: 'Warning' },
  CRITICAL: { fill: theme.palette.error.main, text: theme.palette.error.contrastText, label: 'Critical' },
  NO_DATA: { fill: theme.palette.grey[500], text: theme.palette.common.white, label: 'No data' },
  SENSOR_FAULT: { fill: theme.palette.grey[700], text: theme.palette.common.white, label: 'Sensor fault' },
});

const TyreDiagram = ({
  axles = [], pressureUnit = 'PSI', temperatureUnit = 'C', selected, onSelect,
}) => {
  const theme = useTheme();
  const colours = palette(theme);
  const lane = GEOMETRY.wheel.w + GEOMETRY.gap;
  // Widest axle decides the drawing width, so a trailer axle with four wheels does not get clipped
  // because the steer axle in front of it has two.
  const widest = axles.reduce((most, axle) => Math.max(most, ...axle.wheels.map((w) => Math.abs(OFFSETS[w.side]?.[0] ?? 1))), 1);
  const halfWidth = GEOMETRY.hub + widest * lane + GEOMETRY.margin;
  const width = halfWidth * 2;
  const height = GEOMETRY.margin * 2 + Math.max(1, axles.length) * GEOMETRY.axleGap;
  const centre = halfWidth;

  const x = (side) => {
    const step = OFFSETS[side]?.[0] ?? 1;
    // Half a lane inwards so the inner wheel of a pair sits against the beam rather than on it.
    const magnitude = GEOMETRY.hub + (Math.abs(step) - 0.5) * lane;
    return centre + Math.sign(step) * magnitude - GEOMETRY.wheel.w / 2;
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Tyre layout with pressure and temperature by wheel position"
        sx={{ width: '100%', maxWidth: width, height: 'auto', display: 'block', mx: 'auto' }}
      >
        <title>Tyre layout</title>
        {/* The chassis, so the drawing reads as a vehicle seen from above rather than a grid. */}
        <rect
          x={centre - GEOMETRY.hub}
          y={GEOMETRY.margin - 10}
          width={GEOMETRY.hub * 2}
          height={height - GEOMETRY.margin * 2 + 20}
          rx={10}
          fill={theme.palette.action.hover}
          stroke={theme.palette.divider}
        />
        <text x={centre} y={16} textAnchor="middle" fontSize="13" fontWeight="700" fill={theme.palette.text.secondary}>FRONT</text>
        <text x={centre} y={height - 4} textAnchor="middle" fontSize="13" fontWeight="700" fill={theme.palette.text.secondary}>REAR</text>

        {axles.map((axle, index) => {
          const y = GEOMETRY.margin + index * GEOMETRY.axleGap;
          return (
            <g key={axle.axleNumber}>
              {/* The axle beam, drawn to the outermost wheel on this axle. */}
              <line
                x1={centre - (GEOMETRY.hub + Math.max(...axle.wheels.map((w) => Math.abs(OFFSETS[w.side]?.[0] ?? 1))) * lane)}
                x2={centre + (GEOMETRY.hub + Math.max(...axle.wheels.map((w) => Math.abs(OFFSETS[w.side]?.[0] ?? 1))) * lane)}
                y1={y + GEOMETRY.wheel.h / 2}
                y2={y + GEOMETRY.wheel.h / 2}
                stroke={theme.palette.divider}
                strokeWidth="6"
              />
              <text x={centre} y={y + GEOMETRY.wheel.h / 2 + 4} textAnchor="middle" fontSize="10" fill={theme.palette.text.secondary}>
                {`A${axle.axleNumber}`}
              </text>

              {axle.wheels.map((wheel) => {
                const colour = colours[wheel.health] || colours.NO_DATA;
                const chosen = selected === wheel.position;
                const left = x(wheel.side);
                return (
                  <g
                    key={wheel.position}
                    role="button"
                    tabIndex={0}
                    aria-label={`${wheel.position}, ${colour.label}${wheel.pressure == null ? '' : `, ${wheel.pressure} ${pressureUnit}`}`}
                    onClick={() => onSelect?.(wheel.position)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(wheel.position); } }}
                    style={{ cursor: onSelect ? 'pointer' : 'default' }}
                  >
                    <rect
                      x={left}
                      y={y}
                      width={GEOMETRY.wheel.w}
                      height={GEOMETRY.wheel.h}
                      rx={9}
                      fill={colour.fill}
                      stroke={chosen ? theme.palette.text.primary : theme.palette.divider}
                      strokeWidth={chosen ? 3 : 1}
                    />
                    <text x={left + GEOMETRY.wheel.w / 2} y={y + 17} textAnchor="middle" fontSize="10" fontWeight="700" fill={colour.text}>
                      {wheel.position}
                    </text>
                    <text x={left + GEOMETRY.wheel.w / 2} y={y + 38} textAnchor="middle" fontSize="14" fontWeight="700" fill={colour.text}>
                      {wheel.pressure == null ? '—' : Math.round(wheel.pressure)}
                    </text>
                    <text x={left + GEOMETRY.wheel.w / 2} y={y + 52} textAnchor="middle" fontSize="9" fill={colour.text}>
                      {wheel.pressure == null ? '' : pressureUnit}
                    </text>
                    <text x={left + GEOMETRY.wheel.w / 2} y={y + 67} textAnchor="middle" fontSize="10" fill={colour.text}>
                      {wheel.temperature == null ? '' : `${Math.round(wheel.temperature)}°${temperatureUnit}`}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </Box>

      {/* Said in words as well as colour, for the same reason the wheels carry their state. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
        {['NORMAL', 'WARNING', 'CRITICAL', 'NO_DATA', 'SENSOR_FAULT'].map((state) => (
          <Box key={state} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: '3px', bgcolor: colours[state].fill, border: 1, borderColor: 'divider',
            }}
            />
            <Typography variant="caption" color="text.secondary">{colours[state].label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TyreDiagram;
